import { User } from '@prisma/client';
import { useTranslation } from 'react-i18next';
import {
  API,
  BASE,
  URL_USERS,
  V1,
  apiClient,
  getInitials,
} from '@jobcost-monorepo/utils';
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  Switch,
} from '../../atoms';
import { DropdownMenuSeparator } from '@radix-ui/react-dropdown-menu';
import packageJson from '../../../../apps/web/package.json';
import React, { useEffect, useState } from 'react';
import { IUser } from '@jobcost-monorepo/crud';

interface Props {
  user: User | null;
  onClickLogout?: () => void;
}

const UserMenu: React.FC<Props> = ({ user, onClickLogout }) => {
  const { t } = useTranslation('commons');

  const [darkMode, setDarkMode] = useState<boolean>(
    document.documentElement.classList.contains('dark'),
  );

  const handleChangeDarkMode = async () => {
    setDarkMode(!darkMode);

    const response = await apiClient.patch<IUser>({
      url: `${BASE}${API}${V1}${URL_USERS}/${user?.id}`,
      params: { hasDarkTheme: !darkMode },
    });

    if (!response.ok) {
      setDarkMode(!darkMode);
    }
  };

  const initials = getInitials(user?.firstName || '', user?.lastName);
  const appVersion = packageJson?.version;
  const appBuildDate = packageJson?.buildDate;

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="focus:outline-none">
        <Avatar>
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-max mr-2">
        <div className="flex items-center relative rounded-lg p-2 hover:bg-gray-50 cursor-pointer hover:bg-violet5 dark:hover:bg-violet40">
          <div className="rounded-lg bg-gray-100">
            <Icon
              iconName="user"
              className="fill-black stroke-black dark:fill-white dark:stroke-white"
            />
          </div>
          <span className="w-4" />
          <p className="text-gray-600 font-small">
            {user?.firstName + ' ' + user?.lastName}
          </p>
        </div>
        <DropdownMenuSeparator />
        <div className="flex items-center relative rounded-lg p-2 hover:bg-gray-50 cursor-pointer hover:bg-violet5 dark:hover:bg-violet40">
          <div className="rounded-lg bg-gray-100">
            <Icon
              iconName="info"
              className="fill-black stroke-black dark:fill-white dark:stroke-white"
            />
          </div>
          <span className="w-4" />
          <div className="flex-col justify-end">
            <p className="text-gray-600">
              {t('version')} <span className="font-bold">[{appVersion}]</span>
            </p>
            <p className="font-light text-xs">{appBuildDate}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <div
          className="flex items-center relative rounded-lg p-2 hover:bg-gray-50 cursor-pointer hover:bg-violet5 dark:hover:bg-violet40"
          onClick={handleChangeDarkMode}
        >
          <div className="rounded-lg bg-gray-100">
            <Icon
              iconName={darkMode ? 'moon' : 'sun'}
              className="fill-black stroke-black dark:fill-white dark:stroke-white"
            />
          </div>
          <span className="w-4" />
          <div className="flex flex-row items-center">
            <p className="text-gray-600 mr-4">{t('darkMode')}</p>
            <Switch checked={darkMode} />
          </div>
        </div>
        <DropdownMenuSeparator />
        <div
          onClick={onClickLogout}
          className="flex items-center relative rounded-lg p-2 hover:bg-gray-50 cursor-pointer hover:bg-violet5 dark:hover:bg-violet40"
        >
          <div className="rounded-lg bg-gray-100">
            <Icon
              iconName="logout"
              className="fill-black stroke-black dark:fill-white dark:stroke-white"
            />
          </div>
          <span className="w-4" />
          <p className="text-gray-600 font-small">{t('logout')}</p>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
